import {
  FieldError, FieldPath, FieldValues, UseFormRegister,
} from 'react-hook-form'
import { useState } from 'react'
import { Typography } from '@mui/material'
import { handleOnKeyDown } from 'src/utils/Form'
import Modal from 'src/components/Modal'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import * as S from './ModalQuantityUnfitInShelf.styles'
import { fixInputSx } from 'src/features/restocking/utils'

interface ModalQuantityUnfitInShelfProps<T extends FieldValues, K extends FieldPath<T>> {
  show: boolean
  title: string
  inputName: K
  quantityToRestock: number
  unfitQuantity: number | undefined
  errors: FieldError | undefined
  register: UseFormRegister<T>
  onConfirm: () => void
  onReject: () => any
  errorMessage?: string;
}

const ModalQuantityUnfitInShelf = <T extends FieldValues, K extends FieldPath<T>>(
  props: ModalQuantityUnfitInShelfProps<T, K>,
) => {
  const [showConfirmUnfitQuantityModal, setShowConfirmUnfitQuantityModal] = useState(false)

  const {
    show,
    title,
    onConfirm,
    onReject,
    register,
    inputName,
    quantityToRestock,
    unfitQuantity,
    errors,
    errorMessage,
  } = props

  const openConfirmUnfitQuantityModal = () => {
    const hasErrors = !!errors
    if (unfitQuantity && !hasErrors) setShowConfirmUnfitQuantityModal(true)
  }

  const onSubmitConfirmUnfitQuantityModal = () => {
    onConfirm()
    setShowConfirmUnfitQuantityModal(false)
  }

  const formatQuantity = (quantity: number) => `${quantity} ${quantity > 1 ? 'unidades' : 'unidade'}`

  const getRedirectionText = (quantity: number) => (quantity === 1 ? 'será redirecionada' : 'serão redirecionadas')

  return (
    <Modal
      show={show}
      testId='unfit-quantity-modal-test-id'
      centered
      title={title}
      closeButtonAction={onReject}
      closedButton
      body={(
        <S.Container>
          <Typography>Quantas unidades do produto sobraram?</Typography>
          <S.ContainerInput>
            <S.CustomTextField
              sx={fixInputSx}
              id='unfitQuantity'
              name='unfitQuantity'
              type='number'
              autoComplete='off'
              inputProps={{ ...register(inputName), 'data-testid': 'unfit-input-quantity-test-id' }}
              error={!!errors}
              label='Insira a quantidade que não coube'
              helperText={!!errors && errorMessage}
              fullWidth
              size='medium'
              onKeyDown={(e) => handleOnKeyDown(e, '', openConfirmUnfitQuantityModal)}
            />
          </S.ContainerInput>
          <S.ContainerButton>
            <S.CustomButton
              title='Cancelar'
              bgColor='cancel'
              hoverColor='cancelHover'
              letterColor='white'
              data-testid='cancel-button-test-id'
              onClick={onReject}
            />
            <S.CustomButton
              title='Confirmar'
              bgColor='confirm'
              hoverColor='confirmHover'
              letterColor='white'
              data-testid='confirm-button-test-id'
              disabled={!unfitQuantity || !!errors}
              onClick={openConfirmUnfitQuantityModal}
            />
            {
              unfitQuantity && (
                <ConfirmOrRejectModal
                  show={showConfirmUnfitQuantityModal}
                  title='Confirmar operação'
                  customizableDescription={(
                    <S.ContainerDescription>
                      <p>
                        Ao confirmar a operação, você confirma a movimentação de <strong>{formatQuantity(quantityToRestock - unfitQuantity)} </strong>
                        e <strong> {formatQuantity(unfitQuantity)}</strong> {getRedirectionText(unfitQuantity)} para o excedente.
                      </p>
                      <p>
                        Deseja prosseguir?
                      </p>
                    </S.ContainerDescription>
                  )}
                  onConfirm={onSubmitConfirmUnfitQuantityModal}
                  onReject={() => setShowConfirmUnfitQuantityModal(false)}
                  onClose={() => setShowConfirmUnfitQuantityModal(false)}
                  dataTestId='confirm-unfit-quantity-modal-test-id'
                />
              )
            }

          </S.ContainerButton>
        </S.Container>
        )}
    />
  )
}

export default ModalQuantityUnfitInShelf
